import { Button, Stack, Theme, Typography, useTheme } from "@mui/material";
import { ArrowRightIcon } from "@phosphor-icons/react/dist/csr/ArrowRight";
import { CloudArrowDownIcon } from "@phosphor-icons/react/dist/csr/CloudArrowDown";
import { CloudArrowUpIcon } from "@phosphor-icons/react/dist/csr/CloudArrowUp";
import { KeyIcon } from "@phosphor-icons/react/dist/csr/Key";
import AccentDanger from "../../icons/AccentDanger.svg?react";
import AccentInfo from "../../icons/AccentInfo.svg?react";
import AccentSuccess from "../../icons/AccentSuccess.svg?react";
import Issues from "../../icons/Issues.svg?react";
import { useNavigator } from "../../lib/Hooks";

export type ResourceItem = {
  name: string;
  status: string;
};

export type ResourceGroup = {
  uploads: ResourceItem[];
  downloads: ResourceItem[];
  keys: ResourceItem[];
};

const getIconByStatus = (status: string, theme: Theme) => {
  if (status === "suspended") {
    return (
      <AccentDanger
        width="18px"
        height="18px"
        color={theme.palette.text10.main}
        fill={theme.palette.accentDanger30.main}
      />
    );
  }
  if (status === "blocked") {
    return (
      <Issues width="18px" height="18px" color={theme.palette.text10.main} fill={theme.palette.accentDanger30.main} />
    );
  }
  return (
    <AccentSuccess
      width="18px"
      height="18px"
      color={theme.palette.text10.main}
      fill={theme.palette.accentSuccess30.main}
    />
  );
};

const AccordionRow = ({
  type,
  name,
  status,
}: {
  type: "download" | "upload" | "key";
  name: string;
  status: string;
}) => {
  const navigator = useNavigator();
  const theme = useTheme();
  let iconType;
  switch (type) {
    case "upload":
      iconType = <CloudArrowUpIcon size="24px" />;
      break;
    case "key":
      iconType = <KeyIcon size="24px" />;
      break;
    case "download":
    default:
      iconType = <CloudArrowDownIcon size="24px" />;
      break;
  }
  const handleClickView = () => {
    switch (type) {
      case "download":
      case "upload":
        navigator.goto("homepage-details", { name, type });
        break;
      case "key":
        navigator.goto("keys/details", { keyName: name });
        break;
      default:
        break;
    }
  };
  return (
    <div
      style={{
        display: "grid",
        gap: "10px",
        alignItems: "center",
        justifyContent: "center",
        height: "44px",
        gridTemplateColumns: "30px 1fr 72px",
        flexShrink: 0,
        paddingLeft: "18px",
        paddingRight: "20px",
      }}
    >
      {iconType}
      <Stack direction="row" sx={{ alignItems: "center", gap: "4px" }}>
        {getIconByStatus(status, theme)}
        <Typography variant="body.small.semibold">{name}</Typography>
      </Stack>
      <Button
        size="xSmall"
        variant="text"
        color="secondary"
        endIcon={<ArrowRightIcon size="12px" />}
        onClick={handleClickView}
      >
        View
      </Button>
    </div>
  );
};

const Rollup = ({ uploads, downloads, keys }: ResourceGroup) => {
  const theme = useTheme();
  const allResources = [...uploads, ...downloads, ...keys];

  const countByStatus = (status: string) => {
    return allResources.filter((item) => item.status === status).length;
  };
  return (
    <>
      {["suspended", "blocked", "working"].map((status) => {
        const count = countByStatus(status);
        return count > 0 ? (
          <Stack direction="row" sx={{ gap: "4px", alignItems: "center" }} key={status}>
            <Typography variant="body.medium.semibold" sx={{ color: "text40.main" }}>
              {countByStatus(status)}
            </Typography>
            {getIconByStatus(status, theme)}
          </Stack>
        ) : null;
      })}
    </>
  );
};

const EmptyState = ({ emptyStateText }: { emptyStateText?: string }) => {
  const theme = useTheme();
  return (
    <Stack
      direction="row"
      sx={{
        alignItems: "center",
        justifyContent: "center",
        padding: "20px 40px",
        gap: "10px",
        borderRadius: "8px",
        border: `1px solid ${theme.palette.surfaceLight30.main}`,
        backgroundColor: theme.palette.surfaceLight10.main,
        boxShadow: theme.mixins.shadowSurfaceLight10,
      }}
    >
      <AccentInfo width="24px" height="24px" color={theme.palette.text10.main} fill={theme.palette.accentInfo30.main} />
      <Typography sx={{ color: "text40.main" }} variant="body.large.regular">
        {emptyStateText}
      </Typography>
    </Stack>
  );
};

export { AccordionRow, EmptyState, Rollup };
